// utils/tracking.js - Activity tracking v2 (2026-10)
// Same exports as before (App.js imports unchanged) + initTracking / trackFilter /
// trackTab / trackTicker / trackNews.
//
// Fixes vs previous version:
//  - token was read from localStorage 'token' (never set) -> every event was anonymous.
//    Now uses 'access_token'.
//  - page views only fired on popstate, so in-app navigate() (pushState) was never tracked.
//  - heartbeat every 5 min -> "Online now" (2-min window) could not work. Now 30s.
//  - setupApiTracking re-posted every non-GET API call as an event (duplicating server logs).
//    Now it only tags requests with page/session/visitor headers.
//  - /api/track/batch does not exist; events are now queued and batched to /api/track/event.
//
// Click tracking without code: <tr data-track="ticker_click" data-track-ticker="NVDA">
// (any data-track-* attribute lands in metadata; ticker/label/target/value are top-level)

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const HEARTBEAT_MS = 30000;
const FLUSH_MS = 4000;
const MAX_BATCH = 15;

const PAGE_NAMES = [
  [/^\/$/, 'Landing Page'],
  [/^\/stockwatch/, 'Stock Watch'],
  [/^\/live-watch/, 'Live Watch'],
  [/^\/alerts\/subscription/, 'Alert Subscription'],
  [/^\/alerts/, 'Alerts'],
  [/^\/scalp-watch/, 'ScalpWatch'],
  [/^\/scanner/, 'Signal Scanner'],
  [/^\/reports\/.+/, 'Report'],
  [/^\/reports/, 'Reports'],
  [/^\/admin/, 'Admin Dashboard'],
  [/^\/social-media/, 'Social Media Hub'],
  [/^\/profile/, 'Profile'],
  [/^\/subscription-success/, 'Subscription Success'],
  [/^\/subscription/, 'Pricing / Subscription'],
  [/^\/learn/, 'Learn'],
  [/^\/login/, 'Login'],
  [/^\/register/, 'Registration'],
  [/^\/forgot-password|^\/reset-password/, 'Password Reset'],
  [/^\/pending-approval/, 'Pending Approval'],
  [/^\/contact/, 'Contact'],
  [/^\/legal/, 'Legal'],
];

let queue = [];
let flushTimer = null;
let hbTimer = null;
let started = false;
let lastPath = null;
const filterTimers = {};
const recentClicks = {};

// ---------------------------------------------------------------- identity
const safeGet = (store, key) => { try { return store.getItem(key); } catch { return null; } };
const safeSet = (store, key, v) => { try { store.setItem(key, v); } catch { /* private mode */ } };
const rid = (p) => `${p}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const getVisitorId = () => {
  let id = safeGet(localStorage, 'visitor_id');
  if (!id) {
    id = rid('v');
    safeSet(localStorage, 'visitor_id', id);
    safeSet(localStorage, 'first_visit', new Date().toISOString());
  }
  return id;
};

export const getSessionId = () => {
  let id = safeGet(sessionStorage, 'session_id');
  if (!id) {
    id = rid('s');
    safeSet(sessionStorage, 'session_id', id);
    safeSet(sessionStorage, 'session_start', new Date().toISOString());
  }
  return id;
};

export const getDeviceInfo = () => {
  const ua = navigator.userAgent.toLowerCase();
  let deviceType = 'desktop';
  if (/ipad|tablet/.test(ua)) deviceType = 'tablet';
  else if (/mobile|android|iphone/.test(ua)) deviceType = 'mobile';

  let browser = 'unknown';
  if (ua.includes('edg')) browser = 'Edge';
  else if (ua.includes('opr') || ua.includes('opera')) browser = 'Opera';
  else if (ua.includes('chrome')) browser = 'Chrome';
  else if (ua.includes('firefox')) browser = 'Firefox';
  else if (ua.includes('safari')) browser = 'Safari';

  let os = 'unknown';
  if (ua.includes('windows')) os = 'Windows';
  else if (ua.includes('android')) os = 'Android';
  else if (ua.includes('iphone') || ua.includes('ipad')) os = 'iOS';
  else if (ua.includes('mac')) os = 'macOS';
  else if (ua.includes('linux')) os = 'Linux';

  return {
    device_type: deviceType,
    browser,
    os,
    user_agent: navigator.userAgent,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

const currentPath = () => window.location.pathname;

export const pageNameFor = (path) => {
  for (const [rx, name] of PAGE_NAMES) if (rx.test(path)) return name;
  return path;
};

// ---------------------------------------------------------------- transport
const post = (path, body, { keepalive = false } = {}) => {
  const url = `${API_BASE_URL}${path}`;
  const payload = JSON.stringify(body);
  const headers = { 'Content-Type': 'application/json' };
  const token = safeGet(localStorage, 'access_token');
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { method: 'POST', headers, body: payload, keepalive, credentials: 'omit' })
    .then((r) => {
      // expired token -> resend anonymously so presence/events are not lost
      if (r.status === 401 && headers.Authorization) {
        return fetch(url, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive, credentials: 'omit',
        });
      }
      return r;
    })
    .catch(() => null); // ad blockers / offline: silently skip
};

const flush = (keepalive = false) => {
  if (!queue.length) return;
  const events = queue.splice(0, 50);
  post('/api/track/event', { events, session_id: getSessionId(), visitor_id: getVisitorId() }, { keepalive });
  if (queue.length) flush(keepalive);
};

const scheduleFlush = () => {
  if (queue.length >= MAX_BATCH) { flush(); return; }
  if (!flushTimer) flushTimer = setTimeout(() => { flushTimer = null; flush(); }, FLUSH_MS);
};

const enqueue = (type, props = {}) => {
  if (!type) return null;
  const ev = { ts: Date.now(), ...props };
  Object.keys(ev).forEach((k) => ev[k] === undefined && delete ev[k]);
  ev.type = type;
  ev.page = props.page || currentPath();
  ev.session_id = getSessionId();
  ev.visitor_id = getVisitorId();
  queue.push(ev);
  scheduleFlush();
  return ev;
};

// ---------------------------------------------------------------- public API
// trackPageView(pageName?, page?, extra?) - signature kept from v1
export const trackPageView = async (pageName, page = currentPath(), additionalData = {}) => {
  const title = !pageName || pageName === page || String(pageName).startsWith('/') ? pageNameFor(page) : pageName;
  return enqueue('page_view', { page, title, referrer: document.referrer || undefined, ...additionalData });
};

// trackEvent(name, data) - signature kept from v1.
// Well-known keys (ticker, label, target, value, title) go top-level; the rest -> metadata.
export const trackEvent = async (eventName, eventData = {}) => {
  const { ticker, label, target, value, title, page, ...rest } = eventData || {};
  const type = eventName === 'button_click' ? 'cta_click' : eventName;
  return enqueue(type, {
    ticker, label, target, value, title, page,
    metadata: Object.keys(rest).length ? rest : undefined,
  });
};

export const trackTicker = (ticker, label, type = 'ticker_click') => trackEvent(type, { ticker, label });
export const trackNews = (ticker, headline, url) => trackEvent('news_alert_open', { ticker, label: headline, target: url });
export const trackTab = (scope, tab) => trackEvent('tab_switch', { label: `${scope}:${tab}`, scope, tab });

// Debounced: one event per filter after the user stops changing it.
export const trackFilter = (name, value, { scope, type = 'filter_change', debounceMs = 800, ...extra } = {}) => {
  clearTimeout(filterTimers[name]);
  filterTimers[name] = setTimeout(() => {
    const v = Array.isArray(value) ? value.join('|') : value;
    trackEvent(type, { label: `${name}=${v}`, value: v, filter: name, scope, ...extra });
  }, debounceMs);
};

// Kept for compatibility. Backend already geolocates by IP, so no third-party call.
export const getUserLocation = async () => null;

// Tags API calls with page/session/visitor so server-side logs show where they came from.
export const setupApiTracking = (axiosInstance) => {
  if (!axiosInstance?.interceptors || axiosInstance.__tsTracking) return;
  axiosInstance.__tsTracking = true;
  axiosInstance.interceptors.request.use((config) => {
    config.headers = config.headers || {};
    config.headers['X-Visitor-ID'] = getVisitorId();
    config.headers['X-Session-ID'] = getSessionId();
    config.headers['X-Page-Path'] = currentPath();
    return config;
  });
};

const heartbeat = (visible = document.visibilityState === 'visible', keepalive = false) => {
  const page = currentPath();
  post('/api/track/heartbeat', {
    page, title: pageNameFor(page), visible, session_id: getSessionId(), visitor_id: getVisitorId(),
  }, { keepalive });
};

// startSessionHeartbeat(minutes) - kept; values >= 1 are clamped to 30s so Online Now works.
export const startSessionHeartbeat = (intervalMinutes = 0.5) => {
  const ms = Math.min(intervalMinutes * 60 * 1000, HEARTBEAT_MS);
  if (hbTimer) clearInterval(hbTimer);
  heartbeat(true);
  hbTimer = setInterval(() => { if (document.visibilityState === 'visible') heartbeat(true); }, ms);
  return hbTimer;
};

export const trackTimeOnPage = () => {
  const startTime = Date.now();
  const pagePath = currentPath();
  let sent = false;
  const send = () => {
    if (sent) return;
    sent = true;
    const s = Math.floor((Date.now() - startTime) / 1000);
    if (s > 0) trackEvent('page_time_spent', { page: pagePath, value: s, time_spent_seconds: s });
  };
  window.addEventListener('pagehide', send);
  return () => { window.removeEventListener('pagehide', send); send(); };
};

export const syncStoredEvents = async () => flush();

// ---------------------------------------------------------------- auto tracking
const onRoute = () => {
  const path = currentPath();
  if (path === lastPath) return;
  const prev = lastPath;
  lastPath = path;
  enqueue('page_view', { page: path, title: pageNameFor(path), referrer: prev || document.referrer || undefined });
  if (prev !== null) heartbeat(true); // update "current page" in Online Now immediately
};

const onDocClick = (e) => {
  const el = e.target?.closest?.('[data-track]');
  if (!el) return;
  const type = el.getAttribute('data-track');
  const props = { metadata: {} };
  for (const attr of el.attributes) {
    if (!attr.name.startsWith('data-track-')) continue;
    const k = attr.name.slice('data-track-'.length);
    if (['ticker', 'label', 'target', 'value'].includes(k)) props[k] = attr.value;
    else props.metadata[k] = attr.value;
  }
  if (!props.label) {
    const txt = (el.innerText || el.getAttribute('aria-label') || '').trim().replace(/\s+/g, ' ');
    if (txt) props.label = txt.slice(0, 80);
  }
  if (!Object.keys(props.metadata).length) delete props.metadata;
  const key = `${type}|${props.ticker || ''}|${props.label || ''}`;
  const now = Date.now();
  if (recentClicks[key] && now - recentClicks[key] < 800) return; // row + inner button double fire
  recentClicks[key] = now;
  enqueue(type, props);
};

const onVisibility = () => {
  if (document.visibilityState === 'hidden') {
    flush(true);
    heartbeat(false, true);
  } else {
    heartbeat(true);
  }
};

// Call once at app start (App.js). Idempotent.
export const initTracking = () => {
  if (started || typeof window === 'undefined') return;
  started = true;
  getVisitorId();
  getSessionId();
  ['pushState', 'replaceState'].forEach((fn) => {
    const orig = window.history[fn];
    window.history[fn] = function patched(...args) {
      const ret = orig.apply(this, args);
      setTimeout(onRoute, 0);
      return ret;
    };
  });
  window.addEventListener('popstate', onRoute);
  document.addEventListener('click', onDocClick, true);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', () => { flush(true); heartbeat(false, true); });
  window.addEventListener('online', () => flush());
  startSessionHeartbeat(0.5);
  onRoute();
};

export default {
  getVisitorId,
  getSessionId,
  getDeviceInfo,
  pageNameFor,
  initTracking,
  trackPageView,
  trackEvent,
  trackTicker,
  trackNews,
  trackTab,
  trackFilter,
  getUserLocation,
  setupApiTracking,
  startSessionHeartbeat,
  trackTimeOnPage,
  syncStoredEvents,
};
